import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, ArrowLeft, Copy, Archive, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { createMenu, listMyMenus, duplicateMenu, archiveMenu, deleteMenu, type MenuRow } from "@/lib/menu.functions";
import { useMenuOwnerT } from "@/lib/menu-owner-i18n";

export const Route = createFileRoute("/moje-podniky/$id/menu/")({
  ssr: false,
  component: MenuListPage,
  head: () => ({ meta: [
    { title: "Menu podniku — Cerveza.International" },
    { name: "robots", content: "noindex,nofollow" },
  ] }),
});

const MENU_TYPES = [
  "standing","daily","weekly","breakfast","lunch","dinner",
  "drinks","wine","beer","seasonal","special","custom",
] as const;

function MenuListPage() {
  const { id: businessId } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const t = useMenuOwnerT();
  const listFn = useServerFn(listMyMenus);
  const createFn = useServerFn(createMenu);
  const dupFn = useServerFn(duplicateMenu);
  const archFn = useServerFn(archiveMenu);
  const delFn = useServerFn(deleteMenu);

  const [name, setName] = useState("");
  const [type, setType] = useState<string>("standing");
  const [defLang, setDefLang] = useState("cs");
  const [currency, setCurrency] = useState("EUR");

  const { data: business } = useQuery({
    queryKey: ["business-head", businessId],
    queryFn: async () => {
      const { data } = await supabase.from("businesses").select("id, name").eq("id", businessId).maybeSingle();
      return data;
    },
    enabled: !!businessId,
  });

  const { data: menus = [], isLoading } = useQuery({
    queryKey: ["my-menus", businessId],
    queryFn: () => listFn({ data: { businessId } }),
    enabled: !!user && !!businessId,
  });

  const create = useMutation({
    mutationFn: () => createFn({ data: { businessId, internal_name: name.trim(), menu_type: type, default_language: defLang, currency_code: currency } }),
    onSuccess: (m: MenuRow) => {
      toast.success(t("toast.created"));
      setName("");
      qc.invalidateQueries({ queryKey: ["my-menus", businessId] });
      navigate({ to: "/moje-podniky/$id/menu/$menuId", params: { id: businessId, menuId: m.id } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dup = useMutation({
    mutationFn: (menuId: string) => dupFn({ data: { menuId } }),
    onSuccess: () => { toast.success(t("toast.duplicated")); qc.invalidateQueries({ queryKey: ["my-menus", businessId] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const arch = useMutation({
    mutationFn: (menuId: string) => archFn({ data: { menuId } }),
    onSuccess: () => { toast.success(t("toast.archived")); qc.invalidateQueries({ queryKey: ["my-menus", businessId] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (menuId: string) => delFn({ data: { menuId } }),
    onSuccess: () => { toast.success(t("toast.deleted")); qc.invalidateQueries({ queryKey: ["my-menus", businessId] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (loading) return <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">{t("common.loading")}</div>;
  if (!user) return <div className="grid min-h-screen place-items-center"><Link to="/auth" className="text-primary underline">{t("nav.signIn")}</Link></div>;

  return (
    <div className="min-h-screen bg-muted/30 px-3 py-6 sm:px-4 sm:py-10">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/moje-podniky"><ArrowLeft className="mr-1 h-4 w-4" /> {t("nav.back")}</Link>
            </Button>
            <h1 className="mt-2 font-serif text-2xl sm:text-3xl">{t("list.title")}</h1>
            {business && <p className="text-sm text-muted-foreground">{business.name}</p>}
          </div>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">{t("list.createNew")}</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>{t("list.internalName")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("list.internalNamePh")} />
            </div>
            <div>
              <Label>{t("list.menuType")}</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MENU_TYPES.map((ty) => <SelectItem key={ty} value={ty}>{t(`type.${ty}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("list.mainLang")}</Label>
              <Input value={defLang} onChange={(e) => setDefLang(e.target.value.toLowerCase())} maxLength={5} />
            </div>
            <div>
              <Label>{t("list.currency")}</Label>
              <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} />
            </div>
            <div className="md:col-span-2">
              <Button onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}>
                {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} {t("list.create")}
              </Button>
            </div>
          </CardContent>
        </Card>

        {isLoading && <div className="text-sm text-muted-foreground">{t("common.loading")}</div>}
        {!isLoading && menus.length === 0 && (
          <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">{t("list.empty")}</CardContent></Card>
        )}
        {menus.map((m) => (
          <Card key={m.id}>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Link to="/moje-podniky/$id/menu/$menuId" params={{ id: businessId, menuId: m.id }} className="font-medium hover:underline">
                    {m.internal_name}
                  </Link>
                  <Badge variant="outline">{t(`type.${m.menu_type}`)}</Badge>
                  <Badge>{t(`status.${m.status}`)}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {m.default_language.toUpperCase()} · {m.currency_code} · v{m.version}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {m.status === "published" && (
                  <Button size="sm" variant="outline" asChild>
                    <Link to="/podnik/$id" params={{ id: businessId }}><ExternalLink className="mr-1 h-3 w-3" /> {t("list.publicProfile")}</Link>
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => dup.mutate(m.id)} disabled={dup.isPending}>
                  <Copy className="mr-1 h-3 w-3" /> {t("list.duplicate")}
                </Button>
                {m.status !== "archived" && (
                  <Button size="sm" variant="outline" onClick={() => arch.mutate(m.id)}>
                    <Archive className="mr-1 h-3 w-3" /> {t("list.archive")}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => { if (confirm(t("list.deleteConfirm"))) del.mutate(m.id); }}>
                  <Trash2 className="mr-1 h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
